import React from "react";
import { Field, FieldProps, FieldRenderProps } from "react-final-form";
import { Box, FormControl, FormHelperText, FormLabel } from "@mui/material";
import { FieldValidator } from "final-form";

import { JsonEditor } from "fond/widgets";

export type JsonFieldValue = string | null;
export type JsonFieldProps = {
  name: string;
  label?: string;
  required?: boolean;
  hideError?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  height?: string;
  helperText?: string;
  fieldProps?: Partial<FieldProps<JsonFieldValue, JsonWrapperProps, HTMLElement, string>>;
  validate?: FieldValidator<JsonFieldValue>;
  defaultValue?: string;
};

const JsonField: React.FC<JsonFieldProps> = (props: JsonFieldProps) => {
  const { name, hideError, fieldProps, validate, defaultValue, ...rest } = props;

  return (
    <Field
      name={name}
      render={({ input, meta }) => <JsonFieldWrapper input={input} meta={meta} name={name} hideError={hideError} {...rest} />}
      validate={validate}
      defaultValue={defaultValue}
      validateFields={[]}
      {...fieldProps}
    />
  );
};

type JsonWrapperProps = Pick<JsonFieldProps, "hideError" | "height" | "disabled" | "label" | "required" | "fullWidth" | "helperText"> &
  FieldRenderProps<string, HTMLElement>;

const JsonFieldWrapper: React.FC<JsonWrapperProps> = ({
  input: { name, onChange, value, onFocus, onBlur, ...restInput },
  meta,
  disabled,
  label,
  required,
  fullWidth = true,
  hideError = false,
  height = "200px",
  helperText,
}: JsonWrapperProps) => {
  const hasError = Boolean((meta.error || meta.submitError) && meta.touched);

  return (
    <FormControl fullWidth={fullWidth} data-testid={`${name}-json-field`}>
      {(label || required) && (
        <FormLabel required={required} error={hasError} htmlFor={name} sx={{ marginBottom: 1 }}>
          {label}
        </FormLabel>
      )}
      <Box border={1} borderColor={hasError ? "error.main" : "grey.400"} borderRadius={1} data-testid={`${name}-json-editor`} p={1}>
        <JsonEditor
          value={value || ""}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          height={height}
          disabled={disabled}
          hasError={hasError && !hideError}
        />
      </Box>
      {((hasError && !hideError) || helperText) && (
        <FormHelperText error={hasError && !hideError} data-testid={`${name}-helper-text`}>
          {(!hasError || hideError) && helperText ? helperText : ""}
          {meta.touched && hasError && !hideError && (meta.error || meta.submitError)}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default JsonField;
